import {
  Auth0Client,
  User as Auth0User,
  type IdToken,
} from '@auth0/auth0-spa-js'
import { jwtDecode } from 'jwt-decode'
import type { MyUser } from './api'
import type { LocationQuery } from 'vue-router'

let _auth0: null | Auth0Client = null
export const auth0 = () => {
  if (!_auth0) {
    const config = useRuntimeConfig()
    const localePath = useLocalePath()

    _auth0 = new Auth0Client({
      domain: config.public.auth0Domain,
      clientId: config.public.auth0ClientId,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      authorizationParams: {
        redirect_uri: document.location.origin + '/login',
      },
    })
  }
  return _auth0
}

export const useAccountStore = defineStore('account', () => {
  const api = useAPI()
  const djRefreshToken = useCookie('jwt')
  const djAccessToken: Ref<null | string> = ref(null)
  const auth0User: Ref<null | Auth0User> = ref(null)
  const djangoUser: Ref<null | MyUser> = ref(null)
  const claims: Ref<null | IdToken> = ref(null)
  const idToken = computed(() => claims.value?.__raw)
  const djClaims = computed(() =>
    djAccessToken.value ? jwtDecode(djAccessToken.value) : null,
  )
  const isAuthenticated = computed(() => Boolean(djangoUser.value))

  const init = async () => {
    if (await _refreshToken()) {
      await _fetchDjangoUser()
    } else {
      await checkSession()
    }
    setWatcher()
  }
  const checkSession = async () => {
    try {
      const token = await auth0().getTokenSilently()
      const u = await auth0().getUser()
      console.debug('getTokenSilently', u)

      auth0User.value = u || null

      if (u) {
        const claimsData = await auth0().getIdTokenClaims()
        claims.value = claimsData || null
        await _fetchDjangoUser()
      }
    } catch (err: any) {
      console.debug('getTokenSilently failed', err)
      auth0User.value = null
      claims.value = null
    }
  }
  const handleRedirectCallback = async (q: LocationQuery) => {
    console.debug('handleRedirectCallback', q)
    if (q.code || (q.error && q.state)) {
      try {
        const result = await auth0().handleRedirectCallback()
        await checkSession()
        return result
      } catch (err) {
        console.error(err)
      }
    }
    return null
  }
  const setWatcher = () => {
    watch(auth0User, async (user) => {
      if (user) {
        const token = await auth0().getIdTokenClaims()
        claims.value = token || null
        await _fetchDjangoUser()
      } else {
        claims.value = null
        djangoUser.value = null
      }
    })
    watch(djAccessToken, (token) => {
      if (!token) {
        djangoUser.value = null
      }
    })
  }

  const _fetchDjangoUser = async () => {
    try {
      const user = await api.account.get()
      djangoUser.value = user
    } catch (err) {
      console.error(err)
    }
  }
  const _refreshToken = async () => {
    if (djRefreshToken.value) {
      try {
        const { access } = await api.auth.refreshToken(djRefreshToken.value)
        djAccessToken.value = access
        return true
      } catch (err) {
        console.error(err)
      }
    }
    return false
  }

  const getIdToken = async () => {
    // TODO: tokenが期限切れの場合に再度取得
    return idToken.value
  }
  const getAccessToken = async () => {
    if (!djClaims.value?.exp) return null
    if (djClaims.value.exp * 1000 - 5000 < Date.now()) {
      // NOTE: 5秒以内にaccess tokenの有効期限が切れるのでrefresh
      await _refreshToken()
    }
    return djAccessToken.value
  }

  const loginWithRedirect = (next: string) => {
    return auth0().loginWithRedirect({
      appState: {
        target: next,
      },
    })
  }
  const logout = async () => {
    if (auth0User.value) {
      await auth0().logout()
      auth0User.value = null
      claims.value = null
    }
    if (djRefreshToken.value) {
      djRefreshToken.value = null
      djAccessToken.value = null
    }
  }
  const loginWithDjangoEmailPassword = async (data: {
    email: string
    password: string
  }) => {
    const { access, refresh } = await api.auth.obtainToken(data)
    djRefreshToken.value = refresh
    djAccessToken.value = access
    await _fetchDjangoUser()
  }
  return {
    user: djangoUser,
    djangoClaims: djClaims,
    djangoUser,
    auth0User,
    claims,
    idToken,
    isAuthenticated,
    init,
    handleRedirectCallback,
    getIdToken,
    getAccessToken,
    loginWithRedirect,
    logout,
    loginWithDjangoEmailPassword,
  }
})
